import { Add } from "@mui/icons-material";
import { Accordion, Alert, Box, Button, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { GqlPermissions, GqlProtocolVisit, ProtocolUpdateProtocolVisitInput } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import useProtocolVisitCreate from "../../routes/ProtocolEditRoute/api/useProtocolVisitCreate";
import useProtocolVisitUpdate from "../../routes/ProtocolEditRoute/api/useProtocolVisitUpdate";
import VisitEditForm from "./VisitEditForm";
import VisitSummary from "./VisitSummary";

type Props = {
    protocol: { id: string };
    visits: GqlProtocolVisit[];
};

export default function ProtocolVisitsEditor({ protocol, visits }: Props) {
    const { t } = useTranslation("protocol");
    const [expanded, setExpanded] = useState<string | false>(false);

    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const { addVisit, loading: createLoading, error: createError } = useProtocolVisitCreate(protocol.id);
    const { updateVisit, loading: updateLoading, error: updateError } = useProtocolVisitUpdate(protocol.id);

    const isLoading = isCreatingOrUpdating(createLoading, updateLoading);
    const error = createError ?? updateError;

    const createVisit = async (): Promise<string | null> => {
        const result = await addVisit();
        const newVisitId = result?.protocolVisitCreate?.id;

        if (!newVisitId) {
            return null;
        }

        return newVisitId;
    };

    const handleAddVisit = async () => {
        const newVisitId = await createVisit();

        if (!newVisitId) {
            return;
        }

        setExpanded(newVisitId);
    };

    // The copy is persisted with the values of the original right away, so the summary, the form and a page refresh all show the same values.
    const handleCopyVisit = async (visitToCopy: GqlProtocolVisit) => {
        const newVisitId = await createVisit();

        if (!newVisitId) {
            return;
        }

        const copyInput = createCopyInput(visitToCopy, newVisitId);
        await updateVisit(copyInput);

        setExpanded(newVisitId);
    };

    const handleAccordionChange = (visitId: string, isExpanded: boolean) => {
        if (!isExpanded) {
            setExpanded(false);
            return;
        }

        setExpanded(visitId);
    };

    return (
        <Box>
            {visits.length === 0 && (
                <Box sx={{ p: 6 }}>
                    <Typography variant="h5" align="center">
                        {t("protocolVisitsEditor.emptyText")}
                    </Typography>
                </Box>
            )}
            {visits.map(visit => (
                <Accordion
                    elevation={0}
                    key={visit.id}
                    expanded={expanded === visit.id}
                    onChange={(_, isExpanded) => handleAccordionChange(visit.id, isExpanded)}
                    sx={{ border: 1, borderColor: "divider" }}
                >
                    <VisitSummary
                        visit={visit}
                        allVisits={visits}
                        onCopy={() => handleCopyVisit(visit)}
                        copyLoading={isLoading}
                    />
                    {expanded === visit.id && (
                        <VisitEditForm protocol={protocol} visit={visit} allVisits={visits} onSave={() => setExpanded(false)} />
                    )}
                </Accordion>
            ))}
            {error && (
                <Alert severity="error">
                    <Typography>{error.message}</Typography>
                </Alert>
            )}
            {isAdmin && (
                <Button size="large" sx={{ width: "100%", marginTop: 4 }} onClick={handleAddVisit} loading={isLoading}>
                    <Add />
                    {t("protocolVisitsEditor.btnAdd")}
                </Button>
            )}
        </Box>
    );
}

function isCreatingOrUpdating(createLoading: boolean, updateLoading: boolean): boolean {
    if (createLoading) {
        return true;
    }

    return updateLoading;
}

function createCopyInput(visitToCopy: GqlProtocolVisit, protocolVisitId: string): ProtocolUpdateProtocolVisitInput {
    return {
        protocolVisitId: protocolVisitId,
        notes: visitToCopy.notes,
        condition: createCopyCondition(visitToCopy),
        timeslots: visitToCopy.timeslots,
        minDurationInSeconds: visitToCopy.minDurationInSeconds,
        pricePerHourInCents: visitToCopy.pricePerHourInCents,
        name: visitToCopy.name,
        description: visitToCopy.description,
        calculateTravelCost: visitToCopy.calculateTravelCost,
        minTemperature: visitToCopy.minTemperature,
        maxTemperature: visitToCopy.maxTemperature,
        maxWindForce: visitToCopy.maxWindForce,
        maxPrecipitation: visitToCopy.maxPrecipitation,
    };
}

function createCopyCondition(visitToCopy: GqlProtocolVisit) {
    if (!visitToCopy.condition) {
        return { type: "and", id: uuid() };
    }

    return { ...visitToCopy.condition };
}
